/**
 * Group Initialization Composable
 *
 * Handles all group-related initialization logic including:
 * - Group data fetching based on route changes
 * - Channel state management
 * - Posts auto-fetching for selected channels
 * - Store updates and state synchronization
 *
 * This composable centralizes all the logic that needs to run when:
 * - Group slug changes in the route
 * - Channel changes within a group
 * - Initial page load
 *
 * Usage:
 * ```typescript
 * const { isInitialized, currentGroup, currentChannel } = useGroupInitialization()
 * ```
 */

import { computed, onScopeDispose, onServerPrefetch, ref, watch } from 'vue'

import { useChannels } from '@communities/channels-layer/composables/useChannels'
import { useCommunityUser } from '@communities/community-user-layer/composables/useCommunityUser'
import { communityUserQueryKeys } from '@communities/community-user-layer/utils/query-keys.utils'
import { useInvitations } from '@communities/invitation-layer/composables/useInvitations'
import { useAuth } from '@common/auth-layer/composables/useAuth'
import { onDeferredMounted } from '@common/core-layer/composables/onDeferredMounted'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { useNotificationHelper } from '@common/core-layer/utils/toast.utils'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { usePayment } from '@common/payment-layer/composables/usePayment'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'nuxt/app'
import { useGroups } from './useGroups'
import { GroupMemberRole, MemberStatus } from '../types/types'
import { invalidateCommunitiesMembershipSessionQueries } from '../utils/invalidate-communities-membership-caches'
import { useMembers } from '../../members-layer/composables/useMembers'

export const useGroupInitialization = (initiliseWatchers = false, attribution = 'default') => {
  const { logger } = useLogger()
  const route = useRoute()
  const { store: groupStore, fetchGroupBySlug } = useGroups()
  const { fetchTaxAutomaticConfigQuery } = usePayment()

  // Client-only pre-warm of the per-group `automaticTaxesEnabled` flag.
  // PaymentModal is never rendered during SSR, and the flag isn't needed for
  // initial render. vue-query hooks must register during setup(), so we
  // subscribe here and defer the actual fetch until `onDeferredMounted` sets
  // `warmTaxAutomaticConfig` — scheduling network work during browser idle
  // time after first paint. The factory is keyed on `locationId` with
  // `staleTime: Infinity`, so PaymentModal later subscribes to the same
  // cache entry without a refetch. `enabled` is gated on a truthy
  // locationId, so it auto-fires once the group resolves — no manual watch
  // needed.
  const warmTaxAutomaticConfig = ref(false)
  onDeferredMounted(() => {
    warmTaxAutomaticConfig.value = true
  })

  fetchTaxAutomaticConfigQuery(() => (warmTaxAutomaticConfig.value ? groupStore.currentGroup?.locationId : undefined))

  // Get group slug from route
  const groupSlug = computed(() => route.params.groupSlug as string)
  const groupQuery = fetchGroupBySlug(groupSlug)

  // Track the expected group slug to prevent race conditions
  const expectedGroupSlug = ref<string | null>(groupSlug.value)

  // Track group fetch errors for component-level handling
  const groupFetchError = ref<Error | null>(null)

  // Handle SSR for group query
  onServerPrefetch(async () => {
    const prefetchLabel = `[${attribution}][Group Init] onServerPrefetch`
    const startTime = Date.now()
    if (attribution !== 'default') {
      logger.info(`${prefetchLabel} - start`)
    }

    try {
      if (groupQuery && groupQuery.isLoading?.value) {
        if (attribution !== 'default') {
          logger.debug(`${prefetchLabel}: Suspending group query`)
        }
        await groupQuery.suspense()
      }
      // set data to group store
      groupStore.setCurrentGroup(groupQuery.data.value?.groupDetail || null)
      groupStore.setIsCurrentGroupLoading(false)
      groupStore.setGroupOwner(groupQuery.data.value?.owner)
      groupStore.setInitialized(true)
    } catch (error) {
      logger.error(`${prefetchLabel}: Error during group initialization prefetch:`, {
        error: error as Error,
      })

      // Handle errors gracefully during SSR
      groupStore.setCurrentGroup(null)
      groupStore.setIsCurrentGroupLoading(false)
      groupStore.setInitialized(true)

      // Don't re-throw to prevent SSR crashes
    } finally {
      if (attribution !== 'default') {
        const duration = Date.now() - startTime
        logger.info(`${prefetchLabel} - end`, { payload: { duration: `${duration}ms` } })
      }
    }
  })

  // Initialize channels composable with current group ID
  // For SSR: Use groupId from query data if available, otherwise use store
  const groupId = computed(() => {
    const groupId =
      process.server && groupQuery?.data.value?.groupDetail?._id ? groupQuery.data.value.groupDetail._id : groupStore.currentGroup?._id
    return groupId
  })
  const groupLocationId = computed(() => {
    const groupLocationId = groupStore.currentGroup?.locationId
    return groupLocationId
  })

  const channelsComposable = useChannels(groupId)

  // Initialize community user composable and fetch user group details reactively.
  // Pass the auth getter explicitly — the composable default is `false`, which
  // would leave this observer disabled and the invitation-accept activation
  // watch (below) waiting on a query that never refetches.
  const { store: authStore } = useAuth()
  const communityUser = useCommunityUser()
  const userGroupDetails = communityUser.fetchCurrentUserDetailsForGroup(groupId, groupLocationId, () => authStore.isLoggedIn)
  const { fetchGroupMembersListQuery } = useMembers(groupId, groupLocationId)
  fetchGroupMembersListQuery([GroupMemberRole.ADMIN, GroupMemberRole.OWNER])

  // Track if we're in a channel route
  const isChannelRoute = computed(() => {
    return route.path.includes('/channels/') && route.params.channelSlug
  })

  // Get current channel from the channels list
  const currentChannel = computed(() => {
    try {
      const currentChannelId = channelsComposable.ui.currentChannelId
      if (!currentChannelId.value) return null
      // Handle both ref and direct value cases
      const channelId =
        typeof currentChannelId === 'object' && 'value' in currentChannelId
          ? (currentChannelId as { value: string }).value
          : currentChannelId
      if (!channelId) return null
      return channelsComposable.findChannelById(channelId)
    } catch (error) {
      logger.warn(`[${attribution}][Group Init] Error getting current channel:`, {
        error: error as Error,
      })
      return null
    }
  })

  // Note: Posts are now handled by useGroupPageContext for specific groupId
  // This composable only handles state management and prefetching

  if (initiliseWatchers) {
    // Watch for group slug changes and reset UI state immediately for better reactivity

    watch(
      () => groupSlug.value,
      (newSlug, oldSlug) => {
        if (newSlug && newSlug !== oldSlug) {
          logger.debug(`[${attribution}][Group Init] Group slug changed, resetting UI state for better reactivity`)

          // Set the expected group slug to prevent race conditions
          expectedGroupSlug.value = newSlug

          // Reset group store state immediately to show loading states
          // The queries will automatically refetch if needed based on their stale time
          groupStore.setCurrentGroup(null)
          groupStore.setIsCurrentGroupLoading(true)
          groupStore.setInitialized(false)
          // Set membership questions if available

          groupStore.setCurrentGroupMembershipQuestions(null)

          // Reset both channel and user group state when group slug changes
          channelsComposable.ui.setCurrentChannel(undefined)
          communityUser.ui.setCurrentCommunityUser(null)
        }
      },
      { immediate: false }
    )

    // Watch for group query data changes and update store
    watch(
      () => groupQuery?.data.value,
      groupData => {
        if (groupData?.groupDetail) {
          // Only update if this data is for the currently expected group slug
          // This prevents race conditions where old data overwrites new data
          const currentSlug = groupSlug.value
          const dataSlug = groupData.groupDetail.slug

          if (currentSlug === dataSlug && currentSlug === expectedGroupSlug.value) {
            groupStore.setCurrentGroup(groupData.groupDetail)
            groupStore.setIsCurrentGroupLoading(false)

            // Only keep membership questions in the join flow when the group has them enabled.
            // The API may still return a form payload after the feature is turned off.
            const membershipQuestionsEnabled = !!groupData.groupDetail.settings?.membershipQuestionsEnabled
            if (membershipQuestionsEnabled && groupData.membershipQuestions) {
              groupStore.setCurrentGroupMembershipQuestions(groupData.membershipQuestions)
            } else {
              groupStore.setCurrentGroupMembershipQuestions(null)
            }
            // Set owner info if available
            if (groupData.owner) {
              groupStore.setGroupOwner(groupData.owner)
            }

            groupStore.setInitialized(true)
          }
        }
      },
      { immediate: true }
    )

    // Watch for group query error state and handle gracefully
    watch(
      () => groupQuery?.isError.value,
      isError => {
        if (isError) {
          logger.error(`[${attribution}][Group Init] Group query failed:`, {
            error: groupQuery?.error.value as Error,
          })

          // Handle all errors the same way
          groupStore.setCurrentGroup(null)
          groupStore.setIsCurrentGroupLoading(false)
          groupStore.setInitialized(true)

          // Set error state for component-level handling
          groupFetchError.value = groupQuery?.error.value || new Error('Unknown group fetch error')
        } else {
          // Clear error state when query succeeds
          groupFetchError.value = null
        }
      },
      { immediate: true }
    )

    // Watch for group query loading state changes
    watch(
      () => groupQuery?.isLoading.value,
      isLoading => {
        if (isLoading) {
          groupStore.setIsCurrentGroupLoading(true)
        } else if (!groupQuery?.isError.value) {
          // Only set loading to false if there's no error
          // Error state is handled by the error watcher above
          groupStore.setIsCurrentGroupLoading(false)
        }
      },
      { immediate: true }
    )

    // Note: The watch with immediate: true above should handle SSR data updates

    // Watch for route changes and automatically set channel state
    watch(
      [() => route.params.channelSlug, () => channelsComposable.getChannelsList.data.value],
      ([channelSlug, channels]) => {
        // Only process if we're in a channel route and have channels data
        if (!isChannelRoute.value || !channelSlug || !channels?.length) {
          return
        }

        // Find the channel by slug
        const channel = channels.find((ch: { slug: string }) => ch.slug === channelSlug)

        if (channel) {
          channelsComposable.ui.setCurrentChannel(channel._id)
        } else {
          // Clear current channel if channel not found
          channelsComposable.ui.setCurrentChannel(undefined)
        }
      },
      { immediate: true }
    )

    // Watch for route changes away from channel routes to clear state
    watch(
      () => isChannelRoute.value,
      isChannel => {
        if (!isChannel) {
          channelsComposable.ui.setCurrentChannel(undefined)
        }
      }
    )

    // Watch for channel changes and log posts fetching
    watch(
      () => currentChannel.value?._id,
      channelId => {
        if (channelId && isChannelRoute.value) {
          // Posts queries are now reactive and will automatically refetch
          // when channelId or groupId changes due to computed dependencies
        }
      },
      { immediate: true }
    )

    // Watch for group changes and reset both channel and user group state
    watch(
      () => groupStore.currentGroup?._id,
      (newGroupId, oldGroupId) => {
        if (newGroupId !== oldGroupId) {
          // Clear both channel and user group state when group changes
          channelsComposable.ui.setCurrentChannel(undefined)
          communityUser.ui.setCurrentCommunityUser(null)
        }
      }
    )
    watch(
      () => userGroupDetails.data.value,
      userData => {
        if (userData) {
          communityUser.ui.setCurrentCommunityUser(userData)
        } else {
          communityUser.ui.setCurrentCommunityUser(null)
        }
      },
      { immediate: true }
    )

    // ---------------------------------------------------------------------
    // Invitation acceptance (when the URL carries `?invitationId=...`).
    // Mirrors the legacy `handleEmailInvitations` call in ghl-revex-frontend
    // `GroupContainer.vue`. Owned by group initialization (not the layout) so
    // the whole flow — accept, cache refresh, celebration, redirect — lives
    // next to the membership data it drives.
    // ---------------------------------------------------------------------
    const invitations = useInvitations()
    const { CPNavigateTo } = useCPNavigation()
    const queryClient = useQueryClient()
    const router = useRouter()
    const { t } = useI18n()

    // Capture the toast helper during setup: it resolves the Highrise
    // notification provider via inject, which throws/returns undefined in
    // async continuations (post-await). Mirrors the guard in
    // useGroupMembershipLiveStatus — a missing provider must disable toasts,
    // not break the flow.
    let notifications: ReturnType<typeof useNotificationHelper> | null = null
    try {
      notifications = useNotificationHelper()
    } catch (error) {
      logger.warn(`[${attribution}][Group Init] Notification provider unavailable — invitation toasts are disabled`, {
        error: error as Error,
      })
    }

    const invitationAcceptAttempted = ref(false)
    // Auto-off timer for the invitation-accept confetti burst (mirrors the
    // join flow's 3s window in useGroupJoining).
    let invitationConfettiTimeoutId: ReturnType<typeof setTimeout> | null = null
    // Stop handle for the membership-activation watch armed after a successful
    // accept. Composable-scoped (not function-local) so onScopeDispose can
    // tear it down if the layout unmounts mid-flow.
    let stopActivationWatch: (() => void) | undefined

    /**
     * Accepts a group invitation from the URL. Retries are allowed on 5xx /
     * network failures (invitationId stays in the URL so a refresh retries);
     * the in-flight guard only suppresses duplicate runs while a previous
     * attempt is still in flight or already succeeded — a refresh after a
     * network blip must be able to re-attempt, which would make the client
     * retry fail and leave the membership cache stale.
     * On success the backend returns a `magicLink` URL. Logged-out users are
     * fully redirected to it (it establishes their session — legacy
     * email-invite flow); logged-in users already have a valid session, so
     * the redirect is skipped and caches are refreshed in place instead.
     */
    /**
     * Error path for handleInvitationAcceptance. 4xx carries a human-readable
     * reason (e.g. "Invitation Not Found") — shown as-is; 5xx/network failures
     * get the generic copy. The failed invitationId is cleared from the URL
     * only on definitive failures: 4xx (invalid/expired invite) or the
     * backend's terminal bare-`false` 200 (INVITATION_ACCEPT_FAILED — retry
     * would fail the same way). It stays on 5xx/network errors so a refresh
     * retries the accept.
     */
    const handleInvitationAcceptError = async (error: unknown) => {
      const axiosError = error as { response?: { data?: { message?: unknown }; status?: number } }
      const status = axiosError?.response?.status
      const backendMessage = axiosError?.response?.data?.message
      const isClientError = status !== undefined && status >= 400 && status < 500
      const message =
        isClientError && typeof backendMessage === 'string' && backendMessage
          ? backendMessage
          : t('groupInfo.invitations.acceptFailedDescription')
      logger.error(`[${attribution}][Group Init] Failed to accept invitation`, { error: error as Error })
      notifications?.showError({ title: t('groupInfo.invitations.acceptFailedTitle'), description: message })

      const isTerminalFalse = error instanceof Error && error.message === 'INVITATION_ACCEPT_FAILED'
      if (isTerminalFalse || (status && status >= 400 && status < 500)) {
        const { invitationId: _drop, ...rest } = route.query
        await router.replace({ path: route.path, query: rest, hash: route.hash })
      }
    }

    async function handleInvitationAcceptance(invitationId: string) {
      if (!process.client) return
      if (!invitationId || invitationAcceptAttempted.value) return
      if (!groupId.value || !groupLocationId.value) return

      invitationAcceptAttempted.value = true

      try {
        const magicLink = await invitations.acceptInvitation(invitationId)
        if (magicLink && !authStore.isLoggedIn) {
          window.location.href = magicLink
          return
        }

        if (groupId.value && groupLocationId.value) {
          // Awaited so userGroupDetails (role/status) refetches and the UI
          // (Join → Invite Member, member-only tabs) updates right after accept.
          await queryClient.invalidateQueries({
            queryKey: communityUserQueryKeys.userGroupDetails(groupId.value, groupLocationId.value),
          })
        }
        invalidateCommunitiesMembershipSessionQueries(queryClient)

        // Strip the query param BEFORE arming the activation watch: the watch
        // (immediate) can navigate to group home synchronously, and a later
        // replace targeting the old path would race it and land the user back
        // on the previous page (observed on staging: redirect "resolved" but
        // user stayed on About).
        const { invitationId: _drop, ...rest } = route.query
        await router.replace({ path: route.path, query: rest, hash: route.hash })

        // Celebrate + route once the refetched membership row flips to Active —
        // same semantics as useGroupMembershipLiveStatus' approval handling.
        // immediate: covers the common case where the awaited refetch already
        // returned Active; otherwise fires when polling/refetch lands it.
        // Guard flag: with immediate:true the callback can run before the stop
        // handle assignment lands.
        // TODO(staging-debug): remove activation/redirect logs once verified.
        logger.info(`[${attribution}][Group Init] Invitation accepted — arming activation watch`, {
          groupId: groupId.value,
          groupSlug: groupSlug.value,
          currentStatus: userGroupDetails.data.value?.status,
        })
        let activationHandled = false

        const onMembershipActivated = async () => {
          // In-place accept has no visible navigation — celebrate so the
          // membership change is noticed.
          notifications?.showSuccess({
            title: t('groupInfo.invitations.acceptedTitle'),
            description: t('groupInfo.invitations.acceptedDescription'),
          })
          groupStore.setShowJoinSuccessConfetti(true)
          if (invitationConfettiTimeoutId) clearTimeout(invitationConfettiTimeoutId)
          invitationConfettiTimeoutId = setTimeout(() => {
            groupStore.setShowJoinSuccessConfetti(false)
            invitationConfettiTimeoutId = null
          }, 3000)

          // Route into the group once membership is active — same
          // unconditional redirect as useGroupMembershipLiveStatus'
          // redirectToDiscussion. Private-group accepts need it (About
          // access_denied bounce); when already on home the navigation is a
          // harmless no-op.
          logger.info(`[${attribution}][Group Init] Membership active — redirecting to group home`, {
            groupSlug: groupSlug.value,
            routeName: route.name,
            routePath: route.path,
          })
          if (!groupSlug.value) {
            logger.warn(`[${attribution}][Group Init] Redirect skipped — no groupSlug`)
            return
          }
          try {
            await CPNavigateTo({ name: 'communities-groups-home', params: { groupSlug: groupSlug.value } })
            logger.info(`[${attribution}][Group Init] Redirect resolved`, {
              routeName: route.name,
              routePath: route.path,
            })
          } catch (error: unknown) {
            logger.error(`[${attribution}][Group Init] Redirect failed`, { error: error as Error })
          }
        }

        stopActivationWatch = watch(
          () => userGroupDetails.data.value?.status,
          membershipStatus => {
            if (activationHandled || membershipStatus !== MemberStatus.ACTIVE) return
            activationHandled = true
            stopActivationWatch?.()
            void onMembershipActivated()
          },
          { immediate: true }
        )
      } catch (error: unknown) {
        // Reset the in-flight guard so a later watcher trigger (groupId late
        // load, remount with the same URL) can retry after transient 5xx /
        // network failures. Terminal failures strip invitationId from the URL
        // in handleInvitationAcceptError, so they cannot re-fire anyway.
        invitationAcceptAttempted.value = false
        await handleInvitationAcceptError(error)
      }
    }

    // Fire once the group is loaded and the URL carries an invitationId. Auth
    // state is not a source: logged-out accepts go through the same endpoint
    // and redirect via magicLink.
    watch(
      [() => route.query.invitationId, () => groupId.value],
      ([invitationId]) => {
        if (typeof invitationId === 'string' && invitationId) {
          handleInvitationAcceptance(invitationId)
        }
      },
      { immediate: true }
    )

    onScopeDispose(() => {
      if (invitationConfettiTimeoutId) clearTimeout(invitationConfettiTimeoutId)
      stopActivationWatch?.()
    })
  }
  // Computed properties for easy access
  const isInitialized = computed(() => groupStore.isInitialized)
  const currentGroup = computed(() => groupStore.currentGroup)
  const isLoading = computed(() => groupQuery?.isLoading.value ?? false)
  const error = computed(() => groupQuery?.error.value ?? null)

  // Channel state management functions
  // Note: Channel state is managed by route params, not manually settable
  // Use navigateTo() to change channels instead of manual setters

  const hasCurrentChannel = computed(() => !!currentChannel.value)

  // Watch for user group details changes and update UI store
  // This ensures the UI store is always in sync with the reactive query data

  // Computed property to track when both channels and user group details are ready
  const isGroupDataReady = computed(() => {
    const channelsReady = !channelsComposable.getChannelsList.isLoading.value
    const userGroupReady = !userGroupDetails.isLoading.value
    return channelsReady && userGroupReady
  })

  return {
    // State
    isInitialized,
    currentGroup,
    currentChannel,
    isLoading,
    error,
    isChannelRoute,
    hasCurrentChannel,
    isGroupDataReady,

    // Group data
    groupQuery,

    // User group details
    userGroupDetails,

    // Error state for component-level handling
    groupFetchError,

    // Store access
    groupStore,
  }
}
